"use client";

import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";

type ThemeSettings = {
  colors?: Record<string, unknown>;
  typography?: Record<string, unknown>;
  buttons?: Record<string, unknown>;
  spacing?: Record<string, unknown>;
};

const safeColor = (value: unknown, fallback: string) =>
  typeof value === "string" && /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%deg]+\))$/i.test(value.trim())
    ? value.trim()
    : fallback;

const safeFont = (value: unknown, fallback: string) =>
  typeof value === "string" && /^[a-z0-9 '\-,]+$/i.test(value.trim())
    ? value.trim()
    : fallback;

const fontStack = (value: unknown, fallback: "heading" | "body") => {
  const safe = safeFont(value, fallback === "heading" ? "Plus Jakarta Sans" : "Inter");
  const normalized = safe.replace(/["']/g, "").trim().toLowerCase();
  if (normalized === "plus jakarta sans") {
    return "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', system-ui, sans-serif";
  }
  if (normalized === "inter") {
    return "var(--font-inter), 'Inter', system-ui, sans-serif";
  }
  if (normalized === "georgia" || normalized === "system serif") {
    return "Georgia, 'Times New Roman', serif";
  }
  if (normalized === "system-ui" || normalized === "system sans") {
    return "system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  }
  return `${safe}, system-ui, sans-serif`;
};

const safeRadius = (value: unknown, fallback: number) => {
  const parsed = Number.parseFloat(String(value));
  return `${Number.isFinite(parsed) ? Math.min(999, Math.max(0, parsed)) : fallback}px`;
};

const safeNumber = (value: unknown, fallback: number, min: number, max: number) => {
  const parsed = Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback;
};

const safeLetterSpacing = (value: unknown, fallback: string) =>
  typeof value === "string" && /^-?\d+(?:\.\d+)?(?:em|px)$/.test(value.trim()) ? value.trim() : fallback;

export function SiteThemeBridge() {
  const settings = useQuery(api.theme.getThemeSettings) as ThemeSettings | undefined;
  if (!settings) return null;

  const primary = safeColor(settings.colors?.primary, "#8fa78b");
  const secondary = safeColor(settings.colors?.secondary, "#3f3324");
  const accent = safeColor(settings.colors?.accent, "#9db099");
  const background = safeColor(settings.colors?.background, "#f7f2e7");
  const backgroundAlt = safeColor(settings.colors?.backgroundAlt, "#ffffff");
  const text = safeColor(settings.colors?.text, "#2e271d");
  const textMuted = safeColor(settings.colors?.textMuted, "#635644");
  const border = safeColor(settings.colors?.border, "#e8e2d5");
  const buttonBackground = safeColor(settings.buttons?.primaryBg, accent);
  const buttonText = safeColor(settings.buttons?.primaryText, "#ffffff");
  const headingFont = fontStack(settings.typography?.headingFont, "heading");
  const bodyFont = fontStack(settings.typography?.bodyFont, "body");
  const logoFont = settings.typography?.logoFont
    ? fontStack(settings.typography.logoFont, "heading")
    : headingFont;
  const headingWeight = safeNumber(settings.typography?.headingWeight, 800, 100, 900);
  const bodyWeight = safeNumber(settings.typography?.bodyWeight, 400, 100, 900);
  const headingLetterSpacing = safeLetterSpacing(settings.typography?.headingLetterSpacing, "-0.03em");
  const bodyLineHeight = safeNumber(settings.typography?.bodyLineHeight, 1.65, 1.2, 2.2);
  const buttonRadius = safeRadius(settings.buttons?.borderRadius, 32);
  const buttonPaddingX = safeNumber(settings.buttons?.paddingX, 22, 8, 48);
  const buttonPaddingY = safeNumber(settings.buttons?.paddingY, 11, 4, 24);
  const containerMaxWidth = safeNumber(settings.spacing?.containerMaxWidth, 1280, 768, 1920);
  const sectionPaddingY = safeNumber(settings.spacing?.sectionPaddingY, 68, 32, 140);

  const css = `
    :root:not([data-theme="dark"]) {
      --brand-primary: ${primary};
      --brand-primary-hover: color-mix(in srgb, ${primary} 82%, ${secondary});
      --brand-dark: ${secondary};
      --top-bar-bg: ${accent};
      --btn-primary-bg: ${buttonBackground};
      --btn-primary-hover: ${primary};
      --btn-primary-fg: ${buttonText};
      --bg-main: ${background};
      --header-bg: ${background};
      --preloader-bg: ${background};
      --nav-drawer-bg: ${background};
      --bg-card: ${backgroundAlt};
      --surface-alt: ${backgroundAlt};
      --slate-900: ${text};
      --slate-800: ${secondary};
      --slate-600: ${textMuted};
      --slate-200: ${border};
      --border-subtle: color-mix(in srgb, ${border} 58%, transparent);
      --border-strong: ${border};
      --font-heading: ${headingFont};
      --font-body: ${bodyFont};
      --font-logo: ${logoFont};
      --font-heading-weight: ${headingWeight};
      --font-heading-letter-spacing: ${headingLetterSpacing};
      --font-body-weight: ${bodyWeight};
      --font-body-line-height: ${bodyLineHeight};
      --radius-pill: ${buttonRadius};
      --button-padding-x: ${buttonPaddingX}px;
      --button-padding-y: ${buttonPaddingY}px;
      --container-max-width: ${containerMaxWidth}px;
      --section-padding-y: ${sectionPaddingY}px;
    }
  `;

  return <style data-shaaira-live-theme>{css}</style>;
}
